import { events } from '@/app/events/events';
import { focusGrid } from '@/app/helpers/focusGrid';
import type { JsCellsAccessed, JsCoordinate } from '@/app/quadratic-core-types';
import type { CodeCell } from '@/app/shared/types/codeCell';
import type { PanelTab } from '@/app/ui/menus/CodeEditor/panels/CodeEditorPanelBottom';
import type { EvaluationResult } from '@/app/web-workers/pythonWebWorker/pythonTypes';
import { atom, getDefaultStore } from 'jotai';
import type { SetStateAction } from 'react';

export interface ConsoleOutput {
  stdOut?: string;
  stdErr?: string;
}

export interface CodeEditorState {
  showCodeEditor: boolean;
  escapePressed: boolean;
  loading: boolean;
  codeCell: CodeCell;
  codeString?: string;
  evaluationResult?: EvaluationResult;
  consoleOutput?: ConsoleOutput;
  spillError?: JsCoordinate[];
  panelBottomActiveTab: PanelTab;
  showSnippetsPopover: boolean;
  initialCode?: string;
  editorContent?: string;
  diffEditorContent?: {
    editorContent?: string;
    isApplied: boolean;
  };
  showSaveChangesAlert: boolean;
  cellsAccessed: JsCellsAccessed[] | null;
  waitingForEditorClose?: {
    codeCell: CodeCell;
    showCellTypeMenu: boolean;
    initialCode: string;
    inlineEditor?: boolean;
  };
}

export const defaultCodeEditorState: CodeEditorState = {
  showCodeEditor: false,
  escapePressed: false,
  loading: false,
  codeCell: {
    sheetId: '',
    pos: { x: 1, y: 1 },
    language: 'Python',
    lastModified: 0,
  },
  codeString: undefined,
  evaluationResult: undefined,
  consoleOutput: undefined,
  spillError: undefined,
  panelBottomActiveTab: 'console',
  showSnippetsPopover: false,
  initialCode: undefined,
  editorContent: undefined,
  diffEditorContent: undefined,
  showSaveChangesAlert: false,
  cellsAccessed: null,
  waitingForEditorClose: undefined,
};

const codeEditorBaseAtom = atom<CodeEditorState>(defaultCodeEditorState);

export const codeEditorAtom = atom(
  (get) => get(codeEditorBaseAtom),
  (get, set, update: SetStateAction<CodeEditorState>) => {
    const oldValue = get(codeEditorBaseAtom);
    const newValue = typeof update === 'function' ? update(oldValue) : update;

    if (newValue.showCodeEditor) {
      if (
        newValue.codeCell.sheetId !== oldValue.codeCell.sheetId ||
        newValue.codeCell.pos.x !== oldValue.codeCell.pos.x ||
        newValue.codeCell.pos.y !== oldValue.codeCell.pos.y ||
        newValue.codeCell.language !== oldValue.codeCell.language
      ) {
        events.emit('codeEditorCodeCell', newValue.codeCell);
      }
    }

    if (oldValue.showCodeEditor && !newValue.showCodeEditor) {
      events.emit('codeEditorCodeCell', undefined);
      set(codeEditorBaseAtom, defaultCodeEditorState);
      focusGrid();
      return;
    }

    set(codeEditorBaseAtom, newValue);
  }
);

const createCodeEditorFieldAtom = <T extends keyof CodeEditorState>(key: T) =>
  atom(
    (get) => get(codeEditorAtom)[key],
    (get, set, update: SetStateAction<CodeEditorState[T]>) => {
      const prev = get(codeEditorAtom)[key];
      const newValue =
        typeof update === 'function' ? (update as (prev: CodeEditorState[T]) => CodeEditorState[T])(prev) : update;
      set(codeEditorAtom, { ...get(codeEditorAtom), [key]: newValue });
    }
  );

export const codeEditorShowCodeEditorAtom = createCodeEditorFieldAtom('showCodeEditor');
export const codeEditorEscapePressedAtom = createCodeEditorFieldAtom('escapePressed');
export const codeEditorLoadingAtom = createCodeEditorFieldAtom('loading');
export const codeEditorCodeCellAtom = createCodeEditorFieldAtom('codeCell');
export const codeEditorCodeStringAtom = createCodeEditorFieldAtom('codeString');
export const codeEditorEvaluationResultAtom = createCodeEditorFieldAtom('evaluationResult');
export const codeEditorConsoleOutputAtom = createCodeEditorFieldAtom('consoleOutput');
export const codeEditorSpillErrorAtom = createCodeEditorFieldAtom('spillError');
export const codeEditorPanelBottomActiveTabAtom = createCodeEditorFieldAtom('panelBottomActiveTab');
export const codeEditorShowSnippetsPopoverAtom = createCodeEditorFieldAtom('showSnippetsPopover');
export const codeEditorInitialCodeAtom = createCodeEditorFieldAtom('initialCode');
export const codeEditorDiffEditorContentAtom = createCodeEditorFieldAtom('diffEditorContent');
export const codeEditorShowSaveChangesAlertAtom = createCodeEditorFieldAtom('showSaveChangesAlert');
export const codeEditorCellsAccessedAtom = createCodeEditorFieldAtom('cellsAccessed');
export const codeEditorWaitingForEditorClose = createCodeEditorFieldAtom('waitingForEditorClose');

export const codeEditorEditorContentAtom = atom(
  (get) => get(codeEditorAtom).editorContent,
  (get, set, newValue: string | undefined) => {
    set(codeEditorAtom, {
      ...get(codeEditorAtom),
      diffEditorContent: undefined,
      editorContent: newValue,
    });
  }
);

export const codeEditorShowDiffEditorAtom = atom((get) => {
  const { waitingForEditorClose, diffEditorContent, editorContent } = get(codeEditorAtom);
  return (
    waitingForEditorClose === undefined &&
    diffEditorContent !== undefined &&
    diffEditorContent.editorContent !== editorContent
  );
});

export const codeEditorUnsavedChangesAtom = atom((get) => {
  const { editorContent, codeString } = get(codeEditorAtom);
  return editorContent !== codeString;
});

export const resetCodeEditorState = () => {
  getDefaultStore().set(codeEditorBaseAtom, defaultCodeEditorState);
};
